/**
 * Shared SVG mask helpers for text-shaped clips (used by text-bg-video and banner knock-out).
 */

const SVG_NS = "http://www.w3.org/2000/svg";

function normalizeSvgLetterSpacing(style) {
  const ls = style.letterSpacing;
  if (!ls || ls === "normal") return "0";
  return ls;
}

function resolveTextDirection(styleOrDir) {
  if (typeof styleOrDir === "string") {
    return styleOrDir === "rtl" ? "rtl" : "ltr";
  }
  return styleOrDir?.direction === "rtl" ? "rtl" : "ltr";
}

/** Match textBgVideo: full shorthand helps SVG match laid-out HTML text. */
function applySvgTextTypography(textEl, style) {
  const family = (style.fontFamily || "sans-serif").replace(/"/g, "'");
  const font = [
    style.fontStyle,
    style.fontVariant || "normal",
    style.fontWeight,
    style.fontStretch || "normal",
    style.fontSize,
    style.lineHeight,
    style.fontFamily,
  ].join(" ");
  const direction = resolveTextDirection(style);
  textEl.setAttribute("font", font);
  textEl.setAttribute("font-family", family);
  textEl.setAttribute("font-size", style.fontSize);
  textEl.setAttribute("font-weight", style.fontWeight);
  textEl.setAttribute("font-style", style.fontStyle);
  textEl.setAttribute("letter-spacing", normalizeSvgLetterSpacing(style));
  textEl.setAttribute("dominant-baseline", "alphabetic");
  textEl.setAttribute("text-anchor", "start");
  // Mask SVG lives on <body> and does not inherit html[dir]; without this, RTL glyphs misalign.
  textEl.setAttribute("direction", direction);
  textEl.setAttribute("unicode-bidi", "embed");
  textEl.style.direction = direction;
  textEl.setAttribute("fill", "white");
}

/**
 * Inline <mask> in the live document (not a data: URI) so page @font-face applies to SVG text.
 * Data-URI SVG masks render in an isolated context and fall back to system fonts while DOM
 * measurements use the real webfont — knockout then misaligns.
 */
function ensureBannerKnockoutMask(el) {
  let maskId = el.dataset.bannerKnockoutMaskId;
  if (el._bannerKnockoutMaskSvg?.isConnected) {
    return maskId;
  }
  if (el._bannerKnockoutMaskSvg && !el._bannerKnockoutMaskSvg.isConnected) {
    delete el._bannerKnockoutMaskSvg;
  }
  if (!maskId) {
    maskId = `banner-knockout-mask-${Math.random().toString(36).slice(2, 11)}`;
    el.dataset.bannerKnockoutMaskId = maskId;
  }

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("width", "0");
  svg.setAttribute("height", "0");
  svg.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none";

  const defs = document.createElementNS(SVG_NS, "defs");
  const mask = document.createElementNS(SVG_NS, "mask");
  mask.setAttribute("id", maskId);
  mask.setAttribute("maskUnits", "userSpaceOnUse");
  mask.setAttribute("maskContentUnits", "userSpaceOnUse");

  defs.appendChild(mask);
  svg.appendChild(defs);
  document.body.appendChild(svg);
  el._bannerKnockoutMaskSvg = svg;

  return maskId;
}

function syncBannerKnockoutMask(maskId, segments, w, h, style) {
  const maskEl = document.getElementById(maskId);
  if (!maskEl) return;

  maskEl.setAttribute("x", "0");
  maskEl.setAttribute("y", "0");
  maskEl.setAttribute("width", String(w));
  maskEl.setAttribute("height", String(h));

  while (maskEl.firstChild) {
    maskEl.removeChild(maskEl.firstChild);
  }

  for (const seg of segments) {
    const textEl = document.createElementNS(SVG_NS, "text");
    textEl.setAttribute("x", seg.x.toFixed(2));
    textEl.setAttribute("y", seg.baselineY.toFixed(2));
    applySvgTextTypography(textEl, style);
    textEl.textContent = seg.text;
    maskEl.appendChild(textEl);
  }
}

export function makeLineSegment(textNode, fullText, start, end, mediaRect, direction = "ltr") {
  const r = document.createRange();
  r.setStart(textNode, start);
  r.setEnd(textNode, end);
  const rect = r.getBoundingClientRect();
  const isRtl = resolveTextDirection(direction) === "rtl";
  // text-anchor=start: LTR starts at the left edge, RTL starts at the right edge.
  const anchorX = isRtl ? rect.right : rect.left;
  return {
    text: fullText.slice(start, end).trimEnd(),
    x: anchorX - mediaRect.left,
    baselineY: rect.top - mediaRect.top + rect.height * 0.82,
    direction: isRtl ? "rtl" : "ltr",
  };
}

/**
 * Walk character-by-character using Range to detect visual line breaks.
 * Returns one segment per rendered line with x position and baseline y,
 * both expressed relative to the media element's top-left corner.
 */
export function getLineSegments(textSpan, mediaRect, fontSizePx, direction = "ltr") {
  const textNode = textSpan.firstChild;
  if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return [];

  const fullText = textNode.textContent;
  if (!fullText) return [];

  const resolvedDirection = resolveTextDirection(direction);
  const lineThreshold = fontSizePx * 0.5;
  const segments = [];
  let segStart = 0;
  let segTop = null;

  for (let i = 0; i < fullText.length; i++) {
    const r = document.createRange();
    r.setStart(textNode, i);
    r.setEnd(textNode, i + 1);
    const charRect = r.getBoundingClientRect();

    if (charRect.width === 0 && charRect.height === 0) continue;

    if (segTop === null) {
      segTop = charRect.top;
      segStart = i;
    } else if (charRect.top > segTop + lineThreshold) {
      segments.push(
        makeLineSegment(textNode, fullText, segStart, i, mediaRect, resolvedDirection)
      );
      segTop = charRect.top;
      segStart = i;
    }
  }

  if (segTop !== null) {
    segments.push(
      makeLineSegment(
        textNode,
        fullText,
        segStart,
        fullText.length,
        mediaRect,
        resolvedDirection
      )
    );
  }

  return segments;
}

const FULL_WHITE_MASK = "linear-gradient(#ffffff, #ffffff)";

const MASK_STYLE_PROPS = [
  ["-webkit-mask-image", "mask-image"],
  ["-webkit-mask-repeat", "mask-repeat"],
  ["-webkit-mask-size", "mask-size"],
  ["-webkit-mask-position", "mask-position"],
  ["-webkit-mask-composite", "mask-composite"],
];

/**
 * White field with glyph-shaped holes: full-white mask minus white-on-transparent text mask.
 * (Single SVG white+black often composites to “all white” in engines; two layers are reliable.)
 */
export function applyBannerSubtractKnockoutMask(el, segments, w, h, style) {
  if (!el || w <= 0 || h <= 0 || !segments.length) return;

  const maskId = ensureBannerKnockoutMask(el);
  syncBannerKnockoutMask(maskId, segments, w, h, style);

  const textMaskRef = `url(#${maskId})`;
  const combined = `${FULL_WHITE_MASK}, ${textMaskRef}`;

  el.style.setProperty("-webkit-mask-image", combined);
  el.style.setProperty("-webkit-mask-repeat", "no-repeat, no-repeat");
  el.style.setProperty("-webkit-mask-size", "100% 100%, 100% 100%");
  el.style.setProperty("-webkit-mask-position", "0 0, 0 0");
  el.style.setProperty("-webkit-mask-composite", "destination-out");

  el.style.setProperty("mask-image", combined);
  el.style.setProperty("mask-repeat", "no-repeat, no-repeat");
  el.style.setProperty("mask-size", "100% 100%, 100% 100%");
  el.style.setProperty("mask-position", "0 0, 0 0");
  el.style.setProperty("mask-composite", "subtract");
}

/** Remove inline SVG mask fragment and clear mask CSS from the cutout element (e.g. on teardown). */
export function removeBannerKnockoutMaskDef(hostEl) {
  if (!hostEl) return;

  const svg = hostEl._bannerKnockoutMaskSvg;
  if (svg?.parentNode) {
    svg.parentNode.removeChild(svg);
  }
  delete hostEl._bannerKnockoutMaskSvg;
  delete hostEl.dataset.bannerKnockoutMaskId;

  for (const [webkit, standard] of MASK_STYLE_PROPS) {
    hostEl.style.removeProperty(webkit);
    hostEl.style.removeProperty(standard);
  }
}

/** Safari often reports 0×0 until the first layout pass; poll until we get a real size. */
export function whenHasLayout(media, fn, maxAttempts = 30) {
  let n = 0;
  const check = () => {
    n += 1;
    if ((media.clientWidth > 0 && media.clientHeight > 0) || n >= maxAttempts) {
      fn();
    } else {
      requestAnimationFrame(check);
    }
  };
  requestAnimationFrame(check);
}
